import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FieldTypes,
  NumberRangeInput,
  Text,
  useDebounce,
} from '@common-fe/common-fe';

import { NO_KEY, PermissionKey, YES_KEY } from '@/common/constants';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionState from '@/modules/user/hooks/useGetPermissionState';
import { getStringFromBoolean } from '@/utils/modifiers';

import useAIAdjudicationStore from '../stores/useAIAdjudication.store';

const RangeValueTitle = ({ title }: { title: string }) => (
  <Box width={{ max: '32px' }}>
    <Text
      color="textSecondary"
      textAlign="center"
      weight={500}
    >
      {title}
    </Text>
  </Box>
);

const useAIAdjudicationFields = (disabled: boolean, data?: EmployerDto, currentFormValue?: string) => {
  const { setAIAdjudication } = useAIAdjudicationStore();
  const isAiAdjudicationState = useGetPermissionState(PermissionKey.IS_AI_ADJUDICATION);
  const isAiConfidenceLevelState = useGetPermissionState(PermissionKey.AI_CONFIDENCE_LEVEL);
  const sigisAutoApproveLevelState = useGetPermissionState(PermissionKey.SIGIS_AUTO_APPROVE);

  const aiAdjudication = useMemo(
    () => getStringFromBoolean(data?.overridableProperties?.aiAdjudication),
    [data],
  );
  const aiConfidenceLevel = useMemo(
    () => data?.overridableProperties?.aiConfidenceLevel,
    [data],
  );
  const isSigisAutoApproveEnabled = useMemo(
    () => getStringFromBoolean(data?.overridableProperties?.isSigisAutoApproveEnabled),
    [data],
  );

  const [aiConfidenceLevelState, setAiConfidenceLevelState] = useState(aiConfidenceLevel || 1);
  const debounceAiConfidenceLevel = useDebounce(aiConfidenceLevelState);

  const handleChangeRange = useCallback((value: number) => setAIAdjudication({
    aiConfidenceLevel: value,
  }), [setAIAdjudication]);

  useEffect(() => {
    handleChangeRange(debounceAiConfidenceLevel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceAiConfidenceLevel]);

  useEffect(() => {
    if (aiConfidenceLevel) {
      setAIAdjudication({
        aiConfidenceLevel,
      });
    }
  }, [aiConfidenceLevel, setAIAdjudication]);

  const fields = useMemo(() => [
    ...(isAiAdjudicationState.isHide ? [] : [{
      name: 'aiAdjudication',
      type: FieldTypes.Radio,
      label: 'AI Adjudication',
      defaultValue: aiAdjudication,
      options: [
        {
          label: 'Yes',
          value: 'yes',
        },
        {
          label: 'No',
          value: 'no',
        },
      ],
      disabled: isAiAdjudicationState.isView,
    }]),
    ...(isAiConfidenceLevelState.isHide || (currentFormValue ?? aiAdjudication) === NO_KEY ? [] : [{
      name: 'aiConfidenceLevel',
      type: FieldTypes.Node,
      label: 'Claims Processing Confidence Level',
      value: (
        <NumberRangeInput
          onChange={setAiConfidenceLevelState}
          value={aiConfidenceLevelState}
          step={1}
          min={1}
          max={10}
          disabled={disabled || isAiConfidenceLevelState.isView}
          minValueTitle={(
            <RangeValueTitle title="1 Low" />
          )}
          maxValueTitle={(
            <RangeValueTitle title="10 High" />
          )}
        />
      ),
    }]),
    ...(sigisAutoApproveLevelState.isHide || (currentFormValue ?? aiAdjudication) === NO_KEY ? [] : [{
      name: 'isSigisAutoApproveEnabled',
      type: FieldTypes.Radio,
      label: 'Use SIGIS for Auto Approvals?',
      defaultValue: isSigisAutoApproveEnabled,
      options: [
        {
          label: 'Yes',
          value: YES_KEY,
        },
        {
          label: 'No',
          value: NO_KEY,
        },
      ],
      disabled: sigisAutoApproveLevelState.isView,
    }]),
  ], [aiAdjudication, aiConfidenceLevelState, isAiAdjudicationState, isAiConfidenceLevelState,
    sigisAutoApproveLevelState, isSigisAutoApproveEnabled, disabled, currentFormValue]);

  return fields;
};

export default useAIAdjudicationFields;
